import  { useState, memo, useEffect, useRef } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { Divider, Row, Col, Typography } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { addLastContactedDate, setLastContactedDates } from "../../../Slices/lastContactedDatesSlice";
import { setIsRefetch } from '../../../Slices/salesSlice';
import { InputField, SelectInput, DatePickerField, TextareaField, SwitchField, AntdButton, TimePickerField, } from '../../../blueplanitUtils/InputField';
import CommentField from './CommentField';
import { getOptionsByPath } from '../../../utilsdata';  
import { SCHEDULING_CALL,TYPE_OF_CALL } from '../../../Dropdownmanager';
dayjs.extend(utc);
dayjs.extend(timezone);

const OperationsTeam = memo((props) => {

  const dispatch = useDispatch();

  const { assignToUsers, setFieldValue } = props;
  const { values, handleChange, errors, touched } = useFormikContext();
  const lastContactedDates = useSelector(state => state.lastContactedDates);
  const isRefetch = useSelector((state) => state.sales.isRefetch);
  const NestedDatabucket = useSelector((state) => state.dataBucket.NestedDatabucket);
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails)
  const loginUserName = loginUser
    ? loginUser.firstname + " " + loginUser.lastname
    : '';
  const [emails, setEmails] = useState(values.leadsOPS?.map((ops) => ops.emails) || []);
  const [phonenumbers, setPhonenumbers] = useState(values.leadsOPS?.map((ops) => ops.phonenumber) || []);
  const [remarks, setRemarks] = useState(values.leadsOPS?.map((ops) => ops.remarks) || []);
  const [clientAttendees, setClientAttendees] = useState(values.leadsOPS?.map((ops) => ops.clientAttendees) || []);
  const [blueplanitAttendees, setBlueplanitAttendees] = useState(values.leadsOPS?.map((ops) => ops.blueplanitAttendees) || []);
  const [amsUsedByClient, setAmsUsedByClient] = useState(values.leadsOPS?.map((ops) => ops.amsusedbyClient) || []);
  const [clientRequirements, setClientRequirements] = useState(values.leadsOPS?.map((ops) => ops.clientRequirements) || []);
  const [pros, setPros] = useState(values.leadsOPS?.map((ops) => ops.pros) || []);
  const [cons, setCons] = useState(values.leadsOPS?.map((ops) => ops.cons) || []);
  const [localErrors, setLocalErrors] = useState([]);

  useEffect(() => {
    setEmails(values.leadsOPS?.map((ops) => ops.emails) || []);
    setPhonenumbers(values.leadsOPS?.map((ops) => ops.phonenumber) || []);
    setRemarks(values.leadsOPS?.map((ops) => ops.remarks) || []);
    setClientAttendees(values.leadsOPS?.map((ops) => ops.clientAttendees) || []);
    setBlueplanitAttendees(values.leadsOPS?.map((ops) => ops.blueplanitAttendees) || []);
    setAmsUsedByClient(values.leadsOPS?.map((ops) => ops.amsusedbyClient) || []);
    setClientRequirements(values.leadsOPS?.map((ops) => ops.clientRequirements) || []);
    setPros(values.leadsOPS?.map((ops) => ops.pros) || []);
    setCons(values.leadsOPS?.map((ops) => ops.cons) || []);
  }, [values.leadsOPS]);


  useEffect(() => {
    if (isRefetch) {
      setEmails([]);
      setPhonenumbers([]);
      setRemarks([]);
      setClientAttendees([]);
      setBlueplanitAttendees([]);
      setAmsUsedByClient([]);
      setClientRequirements([]);
      setPros([]);
      setCons([]);
    }
    dispatch(setIsRefetch(false))
  }, [isRefetch])

  // Debounced sync local state → Formik (prevents data loss on page navigation)
  const syncTimeoutRef = useRef(null);
  const syncFields = (e, p, r, ca, ba, ams, cr, pr, co) => {
    const fields = [
      { arr: e, key: "emails" }, { arr: p, key: "phonenumber" },
      { arr: r, key: "remarks" }, { arr: ca, key: "clientAttendees" },
      { arr: ba, key: "blueplanitAttendees" }, { arr: ams, key: "amsusedbyClient" },
      { arr: cr, key: "clientRequirements" }, { arr: pr, key: "pros" }, { arr: co, key: "cons" },
    ];
    fields.forEach(({ arr, key }) => {
      arr.forEach((value, index) => {
        if (values?.leadsOPS?.[index]?.[key] !== value) setFieldValue(`leadsOPS[${index}].${key}`, value, false);
      });
    });
  };

  useEffect(() => {
    clearTimeout(syncTimeoutRef.current);
    syncTimeoutRef.current = setTimeout(() => {
      syncFields(emails, phonenumbers, remarks, clientAttendees, blueplanitAttendees, amsUsedByClient, clientRequirements, pros, cons);
    }, 200);
    return () => clearTimeout(syncTimeoutRef.current);
  }, [emails, phonenumbers, remarks, clientAttendees, blueplanitAttendees, amsUsedByClient, clientRequirements, pros, cons]);

  // Flush on unmount — capture current values to avoid stale closures
  useEffect(() => {
    return () => {
      clearTimeout(syncTimeoutRef.current);
      syncFields(emails, phonenumbers, remarks, clientAttendees, blueplanitAttendees, amsUsedByClient, clientRequirements, pros, cons);
    };
  }, [emails, phonenumbers, remarks, clientAttendees, blueplanitAttendees, amsUsedByClient, clientRequirements, pros, cons])

  const handleAddRow = (arrayHelpers) => {

    const newDate = dayjs();
    setFieldValue("lastContactedDate", newDate);
    arrayHelpers.push({
      date: newDate || "",
      time: dayjs().set('second', 0).set('millisecond', 0) || '',
      callType: '',
      emails: '',
      phonenumber: '',
      callScheduledBy: loginUserName,
      remarks: '',
      callConnected: false,
      clientAttendees: '',
      blueplanitAttendees: '',
      amsusedbyClient: '',
      clientRequirements: '',
      pros: '',
      cons: '',
      emailSent: false,
    });

    // Add the new date to Redux state
    dispatch(addLastContactedDate(newDate.toISOString()));
  };

  // Local Validation for Email (onChange)
  const handleEmailChange = (e, index) => {
    const email = e.target.value.trim();
    const newEmails = [...emails];
    newEmails[index] = email;

    let errorMessage = "";

    // Updated email validation (only for '.com' domain)
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9]{2,}$/;
    if (email && !emailRegex.test(email.trim())) {
      errorMessage = "Invalid email format";
    }

    // Remove phone error if email is provided
    const newErrors = [...localErrors];
    newErrors[index] = {
      ...newErrors[index],
      emails: errorMessage,
      phonenumber: email ? "" : newErrors[index]?.phonenumber, // Clear phone error if email is valid
    };

    setEmails(newEmails);
    setLocalErrors(newErrors);
  };

  // Local Validation for Phone Number (onChange)
  const handlePhoneNumberChange = (e, index) => {
    const newPhonenumbers = [...phonenumbers];
    newPhonenumbers[index] = e.target.value;

    setPhonenumbers(newPhonenumbers);
  };

  const handleDeleteRow = (index, arrayHelpers) => {

    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you really want to delete this OTC record?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        arrayHelpers.remove(index); // Remove the call record
        setEmails((prev) => prev.filter((_, i) => i !== index));
        setPhonenumbers((prev) => prev.filter((_, i) => i !== index));
        setRemarks((prev) => prev.filter((_, i) => i !== index));
        setClientAttendees((prev) => prev.filter((_, i) => i !== index));
        setBlueplanitAttendees((prev) => prev.filter((_, i) => i !== index));
        setAmsUsedByClient((prev) => prev.filter((_, i) => i !== index));
        setClientRequirements((prev) => prev.filter((_, i) => i !== index));
        setPros((prev) => prev.filter((_, i) => i !== index));
        setCons((prev) => prev.filter((_, i) => i !== index));

        // Remove the corresponding local errors
        setLocalErrors((prev) => prev.filter((_, i) => i !== index));

        // Get the date of the row being deleted from `leadsCalls`
        const deletedRowDate = arrayHelpers.form.values.leadsOPS[index].date;

        const lastContactedDatess = lastContactedDates.filter(date => dayjs(date).valueOf() !== dayjs(deletedRowDate).valueOf());

        const latestDate = lastContactedDatess.length > 0
          ? Math.max(...lastContactedDatess.map(date => dayjs(date).valueOf()))
          : null;

        dispatch(setLastContactedDates(lastContactedDatess));

        if (latestDate) {
          setFieldValue("lastContactedDate", dayjs(latestDate));
        }
      }
    });

  };

  const getTime = (time) => {
    if (!time) return null;

    if (typeof time === "object" && dayjs.isDayjs(time)) {
      return time;
    }

    if (typeof time === "string") {
      const localTime = dayjs.utc(time).local();
      return localTime;
    }

    return null;
  };
  const getDateValue = (datevalue) => {
    let finalDate = null;
    if (datevalue !== undefined || datevalue !== '') {
      if (typeof datevalue === 'string' && datevalue?.length === 13) {
        finalDate = dayjs(parseInt(datevalue))
      } else if (typeof datevalue === 'object') {
        finalDate = datevalue
      }
    }
    return finalDate;
  }
  return (
    <div style={{ marginTop: '15px', backgroundColor: "#fff", padding: '20px', boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)', borderRadius: '8px' }}>
      <Typography.Title level={5}>Operations Team Communication</Typography.Title>
      <FieldArray
        name="leadsOPS"
        render={(arrayHelpers) => (
          <>
            <Row justify="end" style={{ marginBottom: '8px' }}>
              <AntdButton
                label="Add Row"
                type="primary"
                onClick={() => handleAddRow(arrayHelpers)}
              />
            </Row>
            {values?.leadsOPS?.map((OpsInfo, index) => (
              <div key={index}>
                <Row gutter={16}>
                  <Col span={24} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <Typography.Text strong>Call Schedule No. {index + 1}</Typography.Text>
                    <i
                      className="fa-regular fa-trash-can"
                      style={{
                        color: '#00c5fb',
                        cursor: 'pointer',
                        fontSize: '16px',
                      }}
                      onClick={() => handleDeleteRow(index, arrayHelpers)}
                    />
                  </Col>
                  <Col span={6}>
                    <DatePickerField
                      label="Date"
                      inputReadOnly={true}
                      allowClear={false}
                      format="DD/MM/YYYY"
                      name={`leadsOPS.${index}.date`}
                      value={getDateValue(values.leadsOPS?.[index]?.date)}
                      onChange={(date) => setFieldValue(`leadsOPS.${index}.date`, date)}
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col span={6}>
                    <TimePickerField
                      label="Time"
                      name={`leadsOPS.${index}.time`}
                      value={getTime(OpsInfo?.time)}
                      onChange={(time) =>
                        handleChange({
                          target: { value: time, name: `leadsOPS.${index}.time` },
                        })
                      }
                      format="h:mm A"
                      allowClear={false}
                      inputReadOnly={true}
                      style={{ width: '100%' }}
                    />

                  </Col>
                  <Col span={6}>
                    <SelectInput
                      style={{ width: "100%" }}
                      label="Type of Call"
                      value={values.leadsOPS[index]?.callType || 'Phone Call'}
                      onChange={(value) => setFieldValue(`leadsOPS.${index}.callType`, value)}
                      options={getOptionsByPath(NestedDatabucket, [SCHEDULING_CALL, TYPE_OF_CALL])}
                      placeholder="Select Type of Call"
                    />

                  </Col>
                  <Col span={6}>
                    <InputField
                      label="Emails"
                      name={`leadsOPS.${index}.emails`}
                      value={emails[index] || ''}
                      onChange={(e) => handleEmailChange(e, index)}
                      onBlur={(e) => {
                        handleChange({
                          target: {
                            name: `leadsOPS[${index}].emails`,
                            value: e.target.value.trim(),
                          },
                        });
                      }}
                      error={
                        localErrors[index]?.emails ||
                        (touched?.leadsOPS?.[index]?.emails && errors?.leadsOPS?.[index]?.emails)
                      }
                    />
                  </Col>
                </Row>
                <Row gutter={16}>

                  <Col span={6}>
                    <InputField
                      label="Phone Number"
                      name={`leadsOPS.${index}.phonenumber`}
                      value={phonenumbers[index] || ''}
                      onChange={(e) => handlePhoneNumberChange(e, index)}
                      onBlur={(e) => {
                        handleChange({
                          target: {
                            name: `leadsOPS[${index}].phonenumber`,
                            value: e.target.value,
                          },
                        });
                      }}
                    />
                  </Col>
                  <Col span={6}>
                    <SelectInput
                      style={{ width: "100%" }}
                      label="Call Scheduled by"
                      value={values.leadsOPS[index]?.callScheduledBy || ''}
                      onChange={(value) => setFieldValue(`leadsOPS.${index}.callScheduledBy`, value)}
                      options={assignToUsers?.map((user) => ({
                        key: user.id,
                        label: `${user.firstname} ${user.lastname}`,
                        value: `${user.firstname} ${user.lastname}`
                      }))}
                      placeholder="Select Scheduler"
                      showSearch
                    />
                  </Col>

                  <Col span={6}>
                    <InputField
                      label="Client Attendees"
                      name={`leadsOPS.${index}.clientAttendees`}
                      value={clientAttendees[index] || ''}
                      onChange={(e) => {
                        const newClientAttendees = [...clientAttendees];
                        newClientAttendees[index] = e.target.value;
                        setClientAttendees(newClientAttendees);
                      }}
                      onBlur={(e) => {
                        handleChange({
                          target: {
                            name: `leadsOPS[${index}].clientAttendees`,
                            value: e.target.value,
                          },
                        });
                      }}
                    />
                  </Col>

                  <Col span={6}>
                    <InputField
                      label="Blueplanit Attendees"
                      name={`leadsOPS.${index}.blueplanitAttendees`}
                      value={blueplanitAttendees[index] || ''}
                      onChange={(e) => {
                        const newBlueplanitAttendees = [...blueplanitAttendees];
                        newBlueplanitAttendees[index] = e.target.value;
                        setBlueplanitAttendees(newBlueplanitAttendees);
                      }}
                      onBlur={(e) => {
                        handleChange({
                          target: {
                            name: `leadsOPS[${index}].blueplanitAttendees`,
                            value: e.target.value,
                          },
                        });
                      }}
                    />
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={6}>
                    <InputField
                      label="AMS used by Client"
                      name={`leadsOPS.${index}.amsusedbyClient`}
                      value={amsUsedByClient[index] || ''}
                      onChange={(e) => {
                        const newAmsUsedByClient = [...amsUsedByClient];
                        newAmsUsedByClient[index] = e.target.value;
                        setAmsUsedByClient(newAmsUsedByClient);
                      }}
                      onBlur={(e) => {
                        handleChange({
                          target: {
                            name: `leadsOPS[${index}].amsusedbyClient`,
                            value: e.target.value,
                          },
                        });
                      }}
                    />
                  </Col>
                  <Col span={6} style={{ marginBottom: "10px" }}>
                    <InputField
                      label="Client Requirements"
                      name={`leadsOPS.${index}.clientRequirements`}
                      value={clientRequirements[index] || ''}
                      onChange={(e) => {
                        const newClientRequirements = [...clientRequirements];
                        newClientRequirements[index] = e.target.value;
                        setClientRequirements(newClientRequirements);
                      }}
                      onBlur={(e) => {
                        handleChange({
                          target: {
                            name: `leadsOPS[${index}].clientRequirements`,
                            value: e.target.value,
                          },
                        });
                      }}
                    />
                  </Col>
                  <Col span={6}>
                    <InputField
                      label="Pros"
                      name={`leadsOPS.${index}.pros`}
                      value={pros[index] || ''}
                      onChange={(e) => {
                        const newPros = [...pros];
                        newPros[index] = e.target.value;
                        setPros(newPros);
                      }}
                      onBlur={(e) => {
                        handleChange({
                          target: {
                            name: `leadsOPS[${index}].pros`,
                            value: e.target.value,
                          },
                        });
                      }}
                    />
                  </Col>
                  <Col span={6}>
                    <InputField
                      label="Cons"
                      name={`leadsOPS.${index}.cons`}
                      value={cons[index] || ''}
                      onChange={(e) => {
                        const newCons = [...cons];
                        newCons[index] = e.target.value;
                        setCons(newCons);
                      }}
                      onBlur={(e) => {
                        handleChange({
                          target: {
                            name: `leadsOPS[${index}].cons`,
                            value: e.target.value,
                          },
                        });
                      }}
                    />
                  </Col>
                  <Col span={3}>
                    <SwitchField
                      label="Call Connected"
                      checked={OpsInfo?.callConnected}
                      onChange={(checked) =>
                        handleChange({ target: { name: `leadsOPS.${index}.callConnected`, value: checked } })
                      }
                    />
                  </Col>

                  <Col span={3}>
                    <SwitchField
                      label="Email Sent"
                      checked={OpsInfo?.emailSent}
                      onChange={(checked) =>
                        handleChange({ target: { name: `leadsOPS.${index}.emailSent`, value: checked } })
                      }
                    />
                  </Col>

                  <Col span={24}>
                    <TextareaField
                      label="Remarks"
                      name={`leadsOPS.${index}.remarks`}
                      value={remarks[index] || ''}
                      onChange={(e) => {
                        const newRemarks = [...remarks];
                        newRemarks[index] = e.target.value;
                        setRemarks(newRemarks);
                      }}
                      onBlur={(e) => {
                        handleChange({
                          target: {
                            name: `leadsOPS[${index}].remarks`,
                            value: e.target.value,
                          },
                        });
                      }}
                    />
                  </Col>
                </Row>
                <Divider />
              </div>
            ))}
          </>
        )}
      />
      <CommentField label="Comments" name="opsTeamComments" />
    </div>
  );
});

export default OperationsTeam;

